import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Collapse,
  Avatar,
} from '@mui/material';
import {
  ViewKanban as BoardIcon,
  FormatListBulleted as BacklogIcon,
  Timeline as TimelineIcon,
  Person as ProfileIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  People as MembersIcon,
  ExpandLess,
  ExpandMore,
  Dashboard as OverviewIcon,
  Add as AddIcon,
  Loop as SprintIcon,
  AdminPanelSettings as AdminIcon,
  Payment as BillingIcon,
  CalendarMonth as CalendarIcon,
  BarChart as ReportsIcon,
} from '@mui/icons-material';
import { ROUTES } from '@/router';
import {
  useIsSidebarOpen,
  useUIStore,
  useSelectedWorkspace,
  useWorkspaceProjects,
  useWorkspaces,
  useUser,
  useCurrentOrg,
} from '@/store';
import { WorkspaceSelector } from '@/components/workspace';
import { OrgSelector } from '@/components/organization';

const DRAWER_WIDTH = 260;

/**
 * Sidebar navigation component - Workspace-first design
 */
export const Sidebar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const isSidebarOpen = useIsSidebarOpen();
  const setSidebarOpen = useUIStore((state) => state.setSidebarOpen);
  const selectedWorkspace = useSelectedWorkspace();
  const projects = useWorkspaceProjects();
  const user = useUser();
  const currentOrg = useCurrentOrg();
  const allWorkspaces = useWorkspaces();

  // Workspaces in the current org the user can actually open. Distinct from
  // currentOrg.workspaceCount, which counts every workspace in the org
  // regardless of whether this user is a member of it.
  const accessibleOrgWorkspaces = currentOrg
    ? allWorkspaces.filter((w) => w.organizationId === currentOrg.organizationId).length
    : 0;

  // Owner of the currently selected organization. Compared as strings because
  // the org's ownerId is a UUID while the user store types userId as a number.
  const isOrgOwner = Boolean(
    currentOrg?.ownerId && user?.userId != null &&
    String(currentOrg.ownerId) === String(user.userId)
  );

  // Auto-expand the project that matches the current URL
  const currentProjectKey = location.pathname.match(/\/p\/([^/]+)/)?.[1];

  // Track which project is expanded — initialize from URL, but allow manual toggle
  const [expandedProject, setExpandedProject] = useState<string | null>(currentProjectKey ?? null);

  // Keep expanded in sync when navigating to a new project URL from outside the sidebar
  useEffect(() => {
    if (currentProjectKey && expandedProject !== currentProjectKey) {
      setExpandedProject(currentProjectKey);
    }
  // Only run when the URL project key changes, not when expandedProject changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectKey]);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const isPathStartsWith = (path: string) => location.pathname.startsWith(path);

  const toggleProject = (projectKey: string) => {
    setExpandedProject(prev => prev === projectKey ? null : projectKey);
  };

  const slug = selectedWorkspace?.workspaceSlug;

  const navButtonSx = (_active?: boolean) => ({
    borderRadius: 1,
    py: 0.5,
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': { bgcolor: 'primary.dark' },
      '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
    },
  });

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Toolbar sx={{ minHeight: '56px !important' }}>
        <Typography variant="h6" fontWeight="bold" color="primary" noWrap>
          Track2Excel
        </Typography>
      </Toolbar>
      <Divider />

      {/* Organization Selector */}
      <OrgSelector />
      <Divider />

      {/* Workspace Selector */}
      <WorkspaceSelector />
      <Divider />

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {selectedWorkspace && slug ? (
          <>
            {/* Workspace Navigation */}
            <List sx={{ px: 1, pt: 1 }}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(`/w/${slug}`)}
                  selected={isActive(`/w/${slug}`)}
                  sx={navButtonSx(isActive(`/w/${slug}`))}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <OverviewIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Overview" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
              </ListItem>
            </List>

            {/* Projects Section */}
            <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                letterSpacing={0.5}
                sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                onClick={() => handleNavigation(`/w/${slug}/projects`)}
              >
                PROJECTS
              </Typography>
              {slug && (
                <AddIcon
                  sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                  onClick={() => handleNavigation(`/w/${slug}/projects/new`)}
                />
              )}
            </Box>

            <List sx={{ px: 1, pt: 0 }}>
              {projects.map((project) => {
                const pKey = project.projectKey;
                const isExpanded = expandedProject === pKey;
                const projectBasePath = `/w/${slug}/p/${pKey}`;

                return pKey ? (
                  <Box key={project.groupId}>
                    <ListItem disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => toggleProject(pKey)}
                        selected={isPathStartsWith(projectBasePath)}
                        sx={{
                          ...navButtonSx(isPathStartsWith(projectBasePath)),
                          pr: 1,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              fontSize: '0.7rem',
                              bgcolor: isPathStartsWith(projectBasePath) ? 'primary.contrastText' : 'primary.light',
                              color: isPathStartsWith(projectBasePath) ? 'primary.main' : 'primary.contrastText',
                            }}
                          >
                            {pKey.charAt(0)}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={project.groupName}
                          primaryTypographyProps={{ fontSize: '0.85rem', noWrap: true }}
                        />
                        <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>
                          {pKey}
                        </Typography>
                        {isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                      </ListItemButton>
                    </ListItem>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding sx={{ pl: 2 }}>
                        {[
                          { label: 'Dashboard', icon: <OverviewIcon sx={{ fontSize: 18 }} />, path: projectBasePath },
                          { label: 'Board', icon: <BoardIcon sx={{ fontSize: 18 }} />, path: `${projectBasePath}/board` },
                          { label: 'Backlog', icon: <BacklogIcon sx={{ fontSize: 18 }} />, path: `${projectBasePath}/backlog` },
                          { label: 'Timeline', icon: <TimelineIcon sx={{ fontSize: 18 }} />, path: `${projectBasePath}/timeline` },
                          { label: 'Sprints', icon: <SprintIcon sx={{ fontSize: 18 }} />, path: `${projectBasePath}/sprints` },
                          { label: 'Calendar', icon: <CalendarIcon sx={{ fontSize: 18 }} />, path: `${projectBasePath}/calendar` },
                          { label: 'Reports', icon: <ReportsIcon sx={{ fontSize: 18 }} />, path: `${projectBasePath}/reports` },
                          { label: 'Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: `${projectBasePath}/settings` },
                        ].map((item) => (
                          <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                            <ListItemButton
                              onClick={() => handleNavigation(item.path)}
                              selected={isActive(item.path)}
                              sx={{
                                borderRadius: 1,
                                py: 0.25,
                                '&.Mui-selected': {
                                  bgcolor: 'action.selected',
                                  '&:hover': { bgcolor: 'action.hover' },
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 30, color: isActive(item.path) ? 'primary.main' : 'text.secondary' }}>
                                {item.icon}
                              </ListItemIcon>
                              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                ) : null;
              })}

              {projects.length === 0 && (
                <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    No projects yet
                  </Typography>
                </Box>
              )}
            </List>

            <Divider sx={{ my: 1 }} />

            {/* Workspace Management */}
            <List sx={{ px: 1 }}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(`/w/${slug}/reports`)}
                  selected={isActive(`/w/${slug}/reports`)}
                  sx={navButtonSx(isActive(`/w/${slug}/reports`))}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ReportsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Reports" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(`/w/${slug}/members`)}
                  selected={isActive(`/w/${slug}/members`)}
                  sx={navButtonSx(isActive(`/w/${slug}/members`))}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <MembersIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Members" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(`/w/${slug}/settings`)}
                  selected={isActive(`/w/${slug}/settings`)}
                  sx={navButtonSx(isActive(`/w/${slug}/settings`))}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        ) : (
          /* No workspace selected. Distinguish "this org has none you can open"
             from "just pick one", so switching to an org you have no workspace
             in explains itself instead of showing an empty menu. */
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            {currentOrg && accessibleOrgWorkspaces === 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  No workspaces you can open in <strong>{currentOrg.name}</strong>.
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {currentOrg.workspaceCount > 0
                    ? 'Ask an owner to add you to one, or create your own above.'
                    : 'Create one from the workspace picker above to get started.'}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a workspace to get started
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Bottom section - always visible */}
      <Divider />
      <List sx={{ px: 1, py: 0.5 }}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => handleNavigation(ROUTES.NOTIFICATION)}
            selected={isActive(ROUTES.NOTIFICATION)}
            sx={navButtonSx(isActive(ROUTES.NOTIFICATION))}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <NotificationsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Notifications" primaryTypographyProps={{ fontSize: '0.875rem' }} />
          </ListItemButton>
        </ListItem>
        {/* Org Reports and Billing are organization-owner only — both expose
            data across every workspace in the org. */}
        {isOrgOwner && (
          <>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigation(ROUTES.ORG_REPORTS)}
                selected={isActive(ROUTES.ORG_REPORTS)}
                sx={navButtonSx(isActive(ROUTES.ORG_REPORTS))}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ReportsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Org Reports" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigation('/billing')}
                selected={isActive('/billing')}
                sx={navButtonSx(isActive('/billing'))}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BillingIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Billing" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            </ListItem>
          </>
        )}
        {user?.userRole === 'admin' && (
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation('/admin')}
              selected={isActive('/admin')}
              sx={navButtonSx(isActive('/admin'))}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AdminIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Admin" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigation(ROUTES.PROFILE)}
            selected={isActive(ROUTES.PROFILE)}
            sx={navButtonSx(isActive(ROUTES.PROFILE))}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ProfileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile" primaryTypographyProps={{ fontSize: '0.875rem' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={isSidebarOpen}
        onClose={() => setSidebarOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
